// Web Push Notification Service
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

class WebPushNotificationService {
  private registration: ServiceWorkerRegistration | null = null;
  private subscription: PushSubscription | null = null;

  /**
   * Request notification permission
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return 'denied';
    }

    const permission = await Notification.requestPermission();
    return permission;
  }

  /**
   * Register service worker
   */
  async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service workers are not supported');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/',
      });
      
      console.log('Service Worker registered:', registration);
      this.registration = registration;
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe(): Promise<PushSubscription | null> {
    if (!this.registration) {
      await this.registerServiceWorker();
    }

    if (!this.registration) {
      console.error('Service worker not registered');
      return null;
    }

    try {
      // Ensure service worker is ready before subscribing
      // Wait for the service worker to be in 'activated' state
      await navigator.serviceWorker.ready;
      
      // Double-check registration is still valid
      if (!this.registration || !this.registration.active) {
        console.warn('Service worker not active, waiting...');
        // Wait a bit more for service worker to activate
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Try to get registration again
        const registration = await navigator.serviceWorker.ready;
        if (!registration || !registration.active) {
          console.error('Service worker still not active after waiting');
          return null;
        }
        this.registration = registration;
      }

      // Check if push manager is available
      if (!this.registration.pushManager) {
        console.error('Push manager not available');
        return null;
      }

      // Check if already subscribed
      let subscription = await this.registration.pushManager.getSubscription();
      
      if (subscription) {
        console.log('Already subscribed to push notifications');
        this.subscription = subscription;
        return subscription;
      }

      // Subscribe to push notifications
      if (!VAPID_PUBLIC_KEY) {
        console.error('VAPID public key not configured');
        return null;
      }

      subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
      });

      console.log('Subscribed to push notifications:', subscription);
      this.subscription = subscription;
      
      return subscription;
    } catch (error: any) {
      // Handle specific error types
      if (error.name === 'AbortError' || error.message?.includes('Registration failed')) {
        console.warn('Push subscription failed - service may not be available. This is normal in some browsers/environments.');
        // Don't treat this as a critical error - some browsers/environments don't support push
        return null;
      }
      console.error('Failed to subscribe to push notifications:', error);
      return null;
    }
  }

  /**
   * Get push subscription
   */
  async getSubscription(): Promise<PushSubscription | null> {
    if (!this.registration) {
      await this.registerServiceWorker();
    }

    if (!this.registration) {
      return null;
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      this.subscription = subscription;
      return subscription;
    } catch (error) {
      console.error('Failed to get subscription:', error);
      return null;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(): Promise<boolean> {
    if (!this.subscription) {
      return false;
    }

    try {
      const result = await this.subscription.unsubscribe();
      this.subscription = null;
      return result;
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      return false;
    }
  }

  /**
   * Convert VAPID key from base64 URL to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Get subscription as JSON for sending to backend
   */
  async getSubscriptionJSON(): Promise<string | null> {
    const subscription = await this.getSubscription();
    if (!subscription) {
      return null;
    }

    return JSON.stringify(subscription.toJSON());
  }

  /**
   * Initialize push notifications (request permission, register service worker, subscribe)
   */
  async initialize(): Promise<boolean> {
    try {
      // Check if browser supports push notifications
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.warn('Push notifications are not supported in this browser');
        return false;
      }

      // Request permission
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        console.warn('Notification permission not granted');
        return false;
      }

      // Register service worker
      const registration = await this.registerServiceWorker();
      if (!registration) {
        return false;
      }

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Subscribe to push notifications with retry logic
      let subscription = await this.subscribe();
      
      // If subscription failed, try once more after a short delay
      if (!subscription) {
        console.log('Retrying push subscription after delay...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        subscription = await this.subscribe();
      }

      return subscription !== null;
    } catch (error: any) {
      // Don't log as error if it's a known unsupported scenario
      if (error.name === 'AbortError' || error.message?.includes('Registration failed')) {
        console.warn('Push notifications not available:', error.message);
      } else {
        console.error('Failed to initialize push notifications:', error);
      }
      return false;
    }
  }
}

export default new WebPushNotificationService();

